import type { KeyboardEvent } from 'react';

import { makeCSS } from '@snowit/utils/misc';
import testId from '@snowit/utils/testId';

import Icon from '../Icon';
import LoaderSpinner from '../Loaders/LoaderSpinner';

import cx from 'classnames';
import css from './Avatar.module.scss';

interface IAvatar {
  /** Add a classname to the badge label */
  className?: string;
  /** Id of the component */
  id?: string;
  /** Indicates the user is an Anonymous user */
  isAnonymous?: boolean;
  /** Size of the Avatar */
  size?: number;
  /** loading state */
  loading?: boolean;
  /** Jest/Cypress test string */
  test?: string;
  /** User object */
  user?: {
    name: string;
    surname: string;
    photo?: string;
  };
  onClick?: (...args: any[]) => void;
  editLabel?: string;
  editMode?: boolean;
  color?: string;
  textColor?: string;
}

/**
 Avatar is a visual elements used to show user image.
*/
const Avatar = (props: IAvatar) => {
  const {
    className,
    id,
    size = 2,
    test,
    user,
    onClick,
    editLabel = 'Edit',
    editMode,
    color,
    textColor,
    loading = false,
  } = props;

  const userInitials = [user?.name, user?.surname]
    .filter((f): f is string => Boolean(f))
    .map((str) => str.substring(0, 1).toUpperCase())
    .join(' ');

  const getImage = () => {
    if (user?.photo) {
      return <img className={css.img} src={user.photo} alt={''} />;
    }

    if (userInitials) {
      return <span className={css.initials}>{userInitials}</span>;
    }

    return <Icon className={css.icon} name={'user'} a11y={{ hidden: true }} />;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick(event);
    }
  };

  return (
    <div
      id={id}
      className={cx(css.avatar, className, {
        [css.interactive]: Boolean(onClick),
      })}
      style={makeCSS({
        '--avatar-size': size,
        '--avatar-bg-color': color,
        '--avatar-text-color': textColor,
      })}
      onClick={onClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? editLabel : undefined}
      {...testId(test)}
    >
      {getImage()}
      {loading && (
        <div className={css.loaderWrapper}>
          <LoaderSpinner className={css.loader} />
        </div>
      )}
      {!loading && editMode && (
        <div className={css.btnEdit}>
          <Icon className={css.editIcon} name={'edit'} />
          <span>{editLabel}</span>
        </div>
      )}
    </div>
  );
};

export default Avatar;
